import { Droplet } from "lucide-react";
import type { ReactNode } from "react";
import styles from "./auth-shell.module.css";

export interface AuthShellProps {
  /** The product, above the form in the accent colour, e.g. "Admin console". */
  product: string;
  /** Top left, beside the mark. "MajiTap" until the company is known. */
  companyName?: string;
  /** Top right: who to ask for help. */
  helpLine?: ReactNode;
  children: ReactNode;
}

/**
 * The one sign-in frame for the console and both portals (Auth Shell, `iUIyI` in
 * docs/design/mothership.pen): company at the top, the product above a 400 px form, one step at
 * a time, © MajiTap at the bottom.
 */
export function AuthShell({ product, companyName = "MajiTap", helpLine, children }: AuthShellProps) {
  return (
    <div className={styles.shell}>
      <header className={styles.top}>
        <div className={styles.company}>
          <span className={styles.mark} aria-hidden="true">
            <Droplet size={16} />
          </span>
          <span className={styles.companyName}>{companyName}</span>
        </div>
        {helpLine ? <p className={styles.help}>{helpLine}</p> : null}
      </header>
      <main className={styles.center}>
        <p className={styles.product}>{product}</p>
        {children}
      </main>
      <footer className={styles.footer}>© MajiTap</footer>
    </div>
  );
}

export interface AuthHeadingProps {
  title: string;
  description?: ReactNode;
}

/** The step's title (Geist 28) and one line under it. */
export function AuthHeading({ title, description }: AuthHeadingProps) {
  return (
    <div className={styles.heading}>
      <h1 className={styles.title}>{title}</h1>
      {description ? <p className={styles.description}>{description}</p> : null}
    </div>
  );
}

/** The form under the heading: fields, alert and actions, 24 px apart as in the design. */
export function AuthStack({ children }: { children: ReactNode }) {
  return <div className={styles.stack}>{children}</div>;
}

/** A group of fields, 16 px apart. */
export function AuthFields({ children }: { children: ReactNode }) {
  return <div className={styles.fields}>{children}</div>;
}

/** Quiet copy under the form, e.g. who to ask when a password is forgotten. */
export function AuthNote({ children }: { children: ReactNode }) {
  return <p className={styles.note}>{children}</p>;
}

/** A full-width primary action, as the design draws the sign-in buttons. */
export const authActionClass = styles.action;
